import * as React from 'react';
import autobind from 'autobind-decorator';
import { Button, Drawer, Col, Row, Tabs, Modal, Spin, Tooltip } from 'antd';
import { observable, toJS } from 'mobx';
import { observer } from 'mobx-react';
import * as moment from 'moment';
import { Validator } from 'class-validator';
import { resolve } from 'inversify-react';
import { connect } from "react-redux";
import { CancelTokenSource } from 'axios';

const TabPane = Tabs.TabPane;

import { ObjectUtils } from "src/core/utils/object";
import { DataLocalService } from 'src/core/services/data-local.service';
import { FormatService } from 'src/core/services/format.service';
import { TextView } from 'src/core/ui/text.view';
import { DataTableView, ToolbarVisibility, Column } from 'src/core/ui/data-table.view';
import { TaxEditorController } from 'src/app/general/taxes/parts/tax-editor.controller';
import { SearchProductsController } from 'src/app/bookings/search/search-products.controller';
import { PaxProductOccupancyView } from 'src/app/bookings/quote-entries/parts/pax-product-occupancy.view';
import { OfficeEditorController } from 'src/app/products/office/office-editor.controller';
import { DropdownView } from 'src/core/ui/dropdown.view';
import { DateView, DisableKind } from 'src/core/ui/date.view';
import { ErrataListController } from 'src/app/general/erratas/list/errata-list.controller';
import { ToastNotifications } from 'src/core/ui/toast';
import { ZuluIcon } from 'src/app/shared/icon';
import { NoteView } from '../shared/note.view';
import { ChangeStatusModalView } from 'src/app/bookings/quote-entries/parts/change-status-modal.view';
import { AutoConectTransferController } from 'src/app/bookings/auto-connect-transfer/auto-connect-tranfer.controller'
import { UserTasksModalView } from 'src/app/general/user-tasks/parts/user-tasks-modal';
import { VisibilityEditorView } from 'src/app/shared/parts/visibility-editor.view';
import { TooltipView } from 'src/core/ui/tooltip.view';
import { AccommodationInfoCard } from './parts/accommodation-info-card';
import { TourInfoCard } from './parts/tour-info-card';
import { BuildProductView } from '../product-manually/build-product.view';

interface IQuoteEntryDataSet extends DataSet<QuoteEntryItem> {
	updateOrder: (item: QuoteEntryItem, newOrder: number) => void;
	updateOrders: () => void;
}

interface PropsFromState {
	currencies: CurrencySummary[];
}

interface Props extends ComponentViewProps<IQuoteEntryDataSet> {
	quote: QuoteItem;
	mode: ControlMode;
	formatService: FormatService;
	addProduct: (products: ResultSelection[], search: Search, taxId?: number) => Promise<QuoteEntryItem[]>;
	deleteProducts: (items: QuoteEntryItem[]) => void;
	restoreProducts: (items: QuoteEntryItem[]) => void;
	historyDataService: DataSet<QuoteEntryStateHistory>;
	identityService: any;
	loadStateHistory: (entry: QuoteEntryItem) => void;
	paxs: QuotePaxItem[];
	taxes: TaxSummary[];
	changeStatus: (entry: QuoteEntryItem, observations: string, confirmationNumber: string, rejectedAs?: number) => Promise<void>;
	checkProductDelete: (items: QuoteEntryItem[]) => QuoteEntryItem[];
	rowCss: string;
	getOccupancy: (entry: QuoteEntryItem) => PersonOccupancy[];
	getSearchParameter: (entry: QuoteEntryItem, occupancies: Occupancy[]) => Search;
	addProductManually: (productManually: ProductManually) => Promise<QuoteEntryItem[]>;
	onEditQuoteEntryField: (entries: QuoteEntryItem[], requireUpdatePrice?: boolean, requirePolicyChecking?: boolean) => Promise<void>;
	updateCommissions: () => Promise<void>;
	updateQuote: () => void;
	erratas: ErratasQuoteEntryItem[];
	currencyCode: string;
	autoconnetProductsConfig: { products: AutoConnetProductConfig[] }
	onPinUpSalesmanProfit: (entry: QuoteEntryItem) => Promise<void>;
}

interface State {
	selectedProducts?: ResultSelection[];
	taxId?: number;
	isBusy: boolean;
	cancelSearch?: CancelTokenSource
}

@observer
class QuoteEntryView extends React.Component<Props & PropsFromState, State> {
	public state: State = { isBusy: false };

	@resolve
	formatService: FormatService;

	validator: Validator;

	@resolve
	toast: ToastNotifications;

	@observable
	showSearchModal = false;

	@observable
	showNotesModal = false;

	@observable
	showErratasModal = false;

	@observable
	showStateDialog: QuoteEntryItem;

	@observable
	currentSearch: Search;

	@observable
	searchProducts = false;

	@observable
	disableSearchButton = false;

	@observable
	disableAddButton = false;

	@observable
	rowCss = '';

	erratas: ErrataItem[];

	@observable
	showOccuppancyModal = false;

	@observable
	showAutoConectModal = false;

	@observable
	productManually: ProductManually;

	columns: Column[] = [
		{
			key: 'product',
			dataIndex: 'productDisplayName',
			title: 'Product',
			className: 'product-name',
			render: (text: any, record: QuoteEntryItem) => {
				return (
					<React.Fragment>
						<b>{text}</b>
						{record.confirmationReferenceNumber && <p>Confirmation number: {record.confirmationReferenceNumber}</p>}
					</React.Fragment>
				);
			}
		},
		{
			key: 'dates',
			title: 'Date',
			children: [
				{
					key: 'dateFrom',
					dataIndex: 'dateFrom',
					title: 'From',
					editable: (text, record: QuoteEntryItem, index, isSelected: boolean) => {
						if (isSelected && (record.productId != null || record.externalId != null)) {
							return (
								<DateView
									required
									label={null}
									mode={this.props.mode}
									format="small-date-time"
									value={record.dateFrom}
									showTime={DisableKind.disableSeconds}
									onChanged={(newVal) => {
										if (newVal != null) {
											const days = moment.utc(newVal).diff(moment.utc(record.dateFrom), "day");
											record.dateFrom = newVal;
											record.dateTo = moment.utc(record.dateTo).add(days, "day").toDate();
											record.dynamicProperties.state = 'invalid';
										}
									}}
									dateUsage="utc"
								/>
							);
						}

						return this.props.formatService.toString('small-date-time', text, "utc");
					}
				},
				{
					key: 'dateTo',
					dataIndex: 'dateTo',
					title: 'To',
					editable: (text, record: QuoteEntryItem, index, isSelected: boolean) => {
						if (isSelected && (record.productId != null || record.externalId != null)) {
							return (
								<DateView
									required
									label={null}
									mode={this.props.mode}
									format="small-date-time"
									value={record.dateTo}
									showTime={DisableKind.disableSeconds}
									onChanged={(newVal) => {
										if (newVal != null) {
											record.dateTo = newVal != null ? newVal : record.dateTo;
											record.dynamicProperties.state = 'invalid';
										}
									}}
									dateUsage="utc"
								/>
							);
						}

						return this.props.formatService.toString('small-date-time', text, "utc");
					}
				}
			]
		},
		{
			key: 'prices',
			title: 'Prices',
			children: [
				{
					key: 'cost',
					dataIndex: 'costPrice',
					title: 'Cost',
					editable: (text, record: QuoteEntryItem, index, isSelected: boolean) => {
						if (isSelected && record.productId == null && record.dynamicProperties.productProviderApiName === "ProductBookingSearchService") {
							return (
								<TextView
									label={null}
									mode={this.props.mode}
									value={record.costPrice}
									onChanged={(newVal) => {
										record.costPrice = newVal != null ? parseFloat(newVal) : record.costPrice;
										const cost: Money = {
											currencyIsoCode: record.dynamicProperties.costPriceResult.currencyIsoCode,
											exchangeRate: 1,
											isCommissionable: false,
											isPriceLive: true,
											sourceCurrencyIsoCode: record.dynamicProperties.costPriceResult.currencyIsoCode,
											totalPrice: record.costPrice,
											breakdown: [
												{
													exchangeRate: 1,
													percent: false,
													price: record.costPrice,
													priceType: "PerUnit",
													unitPrice: record.costPrice,
												},
											],
											paxs: record.dynamicProperties.occupancy.map(e => e.id)
										}
										record.dynamicProperties.costPriceResult = cost;
										this.updateQuoteAndCommissionForProductManually();
									}}
								/>
							);
						}

						const costInSaleCurrency = record.costPrice * record.dynamicProperties.sellPriceResult.exchangeRate;
						let content: React.ReactNode = this.renderCurrency(record.dynamicProperties.costPriceResult.currencyIsoCode, text);
						let tooltipTitle: React.ReactNode = `In ${this.props.currencyCode}: ${this.renderCurrency(record.dynamicProperties.sellPriceResult.currencyIsoCode, costInSaleCurrency)}`;
						if (record.dynamicProperties.hasSaleAdjustment && record.dynamicProperties.adjustmentType === 'absolute') {
							content = (
								<span>
									<ZuluIcon type="pushpin" /> {content}
								</span>
							);
							if (record.dynamicProperties.calculatedCostPrice !== record.costPrice) {
								tooltipTitle = (
									<React.Fragment>
										{tooltipTitle}
										<br /> Computed: {this.renderCurrency(record.dynamicProperties.costPriceResult.currencyIsoCode, record.dynamicProperties.calculatedCostPrice)}
									</ React.Fragment>
								);
								content = (
									<span className="mismatch-price-up">
										{content}
									</span>
								);
							}
						}
						return (
							<TooltipView title={tooltipTitle}>
								{content}
							</TooltipView>
						);
					}
				},
				{
					key: 'sell',
					dataIndex: 'sellPrice',
					title: 'Sell',
					editable: (text, record: QuoteEntryItem, index, isSelected: boolean) => {
						if (isSelected && record.productId == null && record.dynamicProperties.productProviderApiName === "ProductBookingSearchService") {
							return (
								<TextView
									label={null}
									mode={this.props.mode}
									value={record.sellPrice}
									onChanged={(newVal) => {
										record.sellPrice = newVal != null ? parseFloat(newVal) : record.sellPrice;
										const sell: Money = {
											currencyIsoCode: record.dynamicProperties.sellPriceResult.currencyIsoCode,
											exchangeRate: 1,
											isCommissionable: false,
											isPriceLive: true,
											sourceCurrencyIsoCode: record.dynamicProperties.sellPriceResult.currencyIsoCode,
											totalPrice: record.sellPrice,
											breakdown: [
												{
													exchangeRate: 1,
													percent: false,
													price: record.sellPrice,
													priceType: "PerUnit",
													unitPrice: record.sellPrice,
												},
											],
											paxs: record.dynamicProperties.occupancy.map(e => e.id)
										}
										record.dynamicProperties.sellPriceResult = sell;
										this.updateQuoteAndCommissionForProductManually();
									}}
								/>
							);
						}

						return this.renderCurrency(record.dynamicProperties.sellPriceResult.currencyIsoCode, text);
					}
				},
				{
					key: 'tax',
					title: 'Tax',
					dataIndex: 'taxId',
					editable: (text, record: QuoteEntryItem, index, isSelected: boolean) => {
						if (isSelected) {
							return (
								<TaxEditorController
									required
									label={null}
									setBusy={this.setBusy}
									mode={this.props.mode}
									value={record.dynamicProperties.taxId}
									onChanged={(newVal) => {
										if (newVal != null) {
											record.dynamicProperties['taxId'] = newVal.id;
											this.props.updateQuote();
										}
									}}
								/>
							);
						}

						const tax = this.props.taxes.find(m => m.id == record.dynamicProperties['taxId']);
						return tax != null ? tax.displayName : '';
					}
				},
				{
					key: 'salePriceGross',
					dataIndex: 'salePriceGross',
					title: 'Sell Gross',
					render: (field: number, record: QuoteEntryItem) => {
						let content: React.ReactNode = this.renderCurrency(record.dynamicProperties.sellPriceResult.currencyIsoCode, record.salePriceGross);
						if (record.dynamicProperties.hasSaleAdjustment && record.dynamicProperties.adjustmentType === 'absolute') {
							content = (
								<span>
									<ZuluIcon type="pushpin" /> {content}
								</span>
							);
							if (record.dynamicProperties.calculatedSalePriceGross !== record.salePriceGross) {
								const computed = this.renderCurrency(record.dynamicProperties.sellPriceResult.currencyIsoCode, ObjectUtils.round(record.dynamicProperties.calculatedSalePriceGross));
								content = (
									<TooltipView title={`Computed: ${computed}`}>
										<span className="mismatch-price-up">
											{content}
										</span>
									</TooltipView>
								);
							}
						}

						return content;
					}
				},
				{
					key: 'salesmanProfit',
					dataIndex: 'salesmanProfit',
					title: 'Salesman Profit',
					editable: (field: number, record: QuoteEntryItem, index, isSelected: boolean) => {
						let content = this.renderCurrency(record.dynamicProperties.sellPriceResult.currencyIsoCode, record.salesmanProfit);
						if (record.dynamicProperties.hasSaleAdjustment) {
							content = (
								<span>
									<ZuluIcon type="pushpin" /> {content}
								</span>
							);
							const salesmanProfit = ObjectUtils.round(record.salesmanProfit);
							const computed = ObjectUtils.round(record.salePriceGross - record.costPrice * record.dynamicProperties.sellPriceResult.exchangeRate);
							const currencyComputed = this.renderCurrency(record.dynamicProperties.sellPriceResult.currencyIsoCode, computed);
							if (salesmanProfit !== computed) {
								content = (
									<TooltipView title={`Computed: ${currencyComputed}`}>
										<span className="mismatch-price-up">
											{content}
										</span>
									</TooltipView>
								);
							}
						} else if (isSelected) {
							return (
								<TooltipView title="Fix salesman profit">
									<Button
										icon="pushpin"
										size="small"
										onClick={() => this.props.onPinUpSalesmanProfit(record)}>
										{content}
									</Button>
								</TooltipView>
							);
						}

						return content;
					}
				}
			]
		},
		{
			key: 'visibility',
			title: 'Visibility',
			dataIndex: 'visibility',
			editable: (text, record: QuoteEntryItem, index, isSelected: boolean) => (
				<VisibilityEditorView
					disabled={this.props.mode == 'display'}
					label={null}
					model={record.dynamicProperties.visibility}
				/>
			)
		},
		{
			key: 'command',
			title: '',
			width: 335,
			render: (text: any, record: QuoteEntryItem, index: number) => {
				let status;
				let style: any = {
				};
				switch (record.status) {
					case 1:
					case 2:
						status = 'primary';
						style.backgroundColor = '#ffffcd';
						style.borderColor = 'black';
						style.color = 'black';
						break;
					case 3:
						status = 'primary';
						style.backgroundColor = '#28a745';
						style.borderColor = '#28a745';
						break;
					case 4:
					case 5:
						status = 'danger';
						break;
				}
				const erratas = this.isVisibleErrataButton(record);
				return (
					<Button.Group>
						<Button
							icon="up"
							onClick={() => this.props.model.updateOrder(record, record.order - 1)}
							disabled={this.props.mode == 'display' || record.order == 1}
						/>
						<Button
							icon="down"
							onClick={() => this.props.model.updateOrder(record, record.order + 1)}
							disabled={this.props.mode == 'display' || Math.max(...this.props.model.items.map(e => e.order)) == record.order}
					/>
						<NoteView
							identityService={this.props.identityService}
							mode={this.props.mode}
							noteProperty="notes"
							record={record.dynamicProperties}
							setBusy={this.setBusy}
						/>
						{
							erratas && <Button
								icon="alert"
								onClick={() => this.showErratasDialog(record)}
							>
							</Button>
						}
						<UserTasksModalView
							setBusy={this.setBusy}
							useLoginFilter={true}
							modelType='quoteEntry'
							modelId={record.id}
							state={record.entity.entityState}
							buttonProps={{ icon: "check-square" }}
							buttonText={null}
							daedline={moment.utc().add(1, "day").toDate()}
							assignedToId={this.props.identityService.userId}
							priority={1}
							titleTask="Task Title"
						/>
						{
							record.dynamicProperties.state && record.dynamicProperties.state === 'invalid' &&
							<Tooltip title="Component needs to refresh its price">
								<Button
									icon="reload"
									onClick={() => this.props.onEditQuoteEntryField([record], true, true)}
									type="danger"
									disabled={this.props.mode == 'display'}
								/>
							</Tooltip>
						}
						<Button
							disabled={this.props.mode == 'display' || this.props.model.isDirty}
							type={status}
							style={style}
							onClick={this.loadStateHistory(record)}
						>
							{record.statusText}
						</Button>
					</Button.Group>
				);
			}
		},
	];

	modificationColumns: Column[] = [
		{
			key: 'product',
			dataIndex: 'productDisplayName',
			title: 'Product',
			className: 'product-name',
			render: (text: any, record: QuoteEntryItem, index: number) => {
				return <b>{text}</b>;
			},
		},
		{
			key: 'cost',
			dataIndex: 'costPrice',
			title: 'Cost',
			render: (text, record: QuoteEntryItem) => this.renderCurrency(record.dynamicProperties.costPriceResult.currencyIsoCode, text)
		},
		{
			key: 'sell',
			dataIndex: 'sellPrice',
			title: 'Price',
			render: (text, record: QuoteEntryItem) => this.renderCurrency(record.dynamicProperties.sellPriceResult.currencyIsoCode, text)
		},
		{
			key: 'margin',
			dataIndex: 'costPrice',
			title: 'Margin',
			render: (text, record: QuoteEntryItem) => {
				const margin = record.salePriceGross - (record.costPrice * record.dynamicProperties.sellPriceResult.exchangeRate);
				return `${ObjectUtils.round(margin)} ${record.dynamicProperties.sellPriceResult.currencyIsoCode}`;
			}
		},
	];

	constructor(props: Readonly<Props & PropsFromState>) {
		super(props);

		this.rowCss = this.props.rowCss;
		this.validator = new Validator();
	}

	componentWillReceiveProps(nextProps: Readonly<Props & PropsFromState>) {
		if (nextProps.rowCss != null) {
			this.rowCss = nextProps.rowCss;
			//this.props.model.items = nextProps.model.items;
		}
	}

	private renderCurrency(currencyCode: string, value: any): React.ReactNode {
		const { formatService, currencies } = this.props;
		const currency = currencies.find(e => e.iso3Name === currencyCode);

		if (!currency)
			return '';

		return formatService.toString('currency', ObjectUtils.round(value, 2), currency.format);
	}

	private getPaxes() {
		return (this.props.paxs || [])
			.slice()
			.sort((x, y) => x.order - y.order)
			.map(e => ({
				id: e.id,
				name: e.formalName,
				age: moment.utc(this.props.quote.reservationDate).diff(moment(e.dynamicProperties['birthDate']), 'year'),
			}));
	}
	@autobind
	private closeAutocconetDialog(ev?: React.MouseEvent<HTMLButtonElement>) {
		if (ev != null) {
			ev.preventDefault();
		}

		this.showAutoConectModal = false;
	}
	renderAutoConnetModal = () => {
		let products = this.props.autoconnetProductsConfig.products;
		let productToconnect = this.props.model.items.filter(e => products.some(p => p.productType == e.productType && (p.categories.length == 0 || p.categories.some(c => c == e.dynamicProperties.categoryId)))).sort((x, y) => moment.utc(x.dateFrom).isAfter(y.dateFrom) ? 1 : -1);
		return (
			<AutoConectTransferController
				closeAutocconetDialog={this.closeAutocconetDialog}
				setBusy={this.setBusy}
				quote={this.props.quote}
				paxs={this.props.paxs}
				formatService={this.props.formatService}
				addProduct={this.props.addProduct}
				showModal={this.showAutoConectModal}
				entries={productToconnect}
			/>
		);
	}
	@autobind
	getErratas(entry: QuoteEntryItem): ErrataItem[] {
		let erratasEntry = this.props.erratas.find(x => x.productId == entry.productId && x.supplierId == entry.supplierId);
		return erratasEntry != null ? erratasEntry.erratas : [];
	}


	private isVisibleErrataButton(entry: QuoteEntryItem) {
		this.erratas = this.getErratas(entry);
		return this.erratas != null && this.erratas.length > 0;
	}
	@autobind
	private showErratasDialog(entry: QuoteEntryItem, ev?: React.MouseEvent<HTMLButtonElement>) {
		if (ev != null) {
			ev.preventDefault();
		}

		this.showErratasModal = true;
		this.erratas = this.getErratas(entry);
	}

	@autobind
	private acceptErratasDialog(ev?: React.MouseEvent<HTMLButtonElement>) {
		if (ev != null) {
			ev.preventDefault();
		}

		this.erratas = null;
		this.showErratasModal = false;
	}

	@autobind
	private closeErratasDialog(ev?: React.MouseEvent<HTMLButtonElement>) {
		if (ev != null) {
			ev.preventDefault();
		}
		this.erratas = null;
		this.showErratasModal = false;
	}

	private renderErratasModal = () => (
		<Modal
			style={{ top: 20 }}
			width="80%"
			visible={this.showErratasModal}
			onCancel={this.acceptErratasDialog}
			title="Erratas"
			maskClosable={false}
			footer={[
				<Button
					key="confirm"
					type="primary"
					onClick={this.acceptErratasDialog}
				>
					OK
				</Button>
			]}>
			{
				this.erratas &&
				<ErrataListController
					readOnly
					model={this.erratas}
					mode="display"
					identityService={this.props.identityService}
					setBusy={this.setBusy}
				/>
			}
		</Modal>
	);

	@autobind
	private async acceptSearchDialog(ev?: React.MouseEvent<HTMLButtonElement>) {
		if (ev != null) {
			ev.preventDefault();
		}

		if (this.productManually) {
			let msg = this.validateProductManually();
			if (msg.length == 0) {
				this.setBusy(true);
				let item = await this.props.addProductManually(this.productManually);
				this.productManually = undefined;
				this.showSearchModal = false;
				this.setBusy(false);
			} else {
				this.toast.showToast("Incorrect Data for Product Manual", msg, 'error');
			}
		} else {
			if (this.state.selectedProducts) {
				this.setBusy(true);
				let item = await this.props.addProduct(this.state.selectedProducts, this.currentSearch, this.state.taxId);
				this.setState({ selectedProducts: undefined, taxId: undefined });
				this.searchProducts = false;
				this.showSearchModal = false;
				this.setBusy(false);
			}
		}
	}

	@autobind
	private closeSearchDialog(ev?: React.MouseEvent<HTMLButtonElement>) {
		if (ev != null) {
			ev.preventDefault();
		}

		this.showSearchModal = false;
		this.setState({ selectedProducts: undefined, taxId: undefined });
		this.searchProducts = false;
		this.productManually = undefined;
	}

	private closeStateDialog = (ev?: React.MouseEvent<HTMLButtonElement>) => {
		if (ev != null) {
			ev.preventDefault();
		}
		this.showStateDialog = null;
	}

	private loadStateHistory = (item: QuoteEntryItem) => () => {
		this.props.loadStateHistory(item);
		this.showStateDialog = item;
	}

	private renderQuoteEntryStatusModal = () => {
		return (
			<ChangeStatusModalView
				quoteStatus={this.props.quote.status}
				setBusy={this.setBusy}
				mode={this.props.mode}
				closeStateDialog={this.closeStateDialog}
				showStateDialog={this.showStateDialog}
				changeStatus={this.props.changeStatus}
				historyDataService={this.props.historyDataService}
				formatService={this.props.formatService}
			/>
		);
	};

	renderModifications(item: QuoteEntryItem) {
		const items = this.props.model.items.filter(e => e.parentEntryId === item.id && e.productType > 5);
		const dataSet = new DataLocalService(items as any);
		return items.length > 0
			? (
				<DataTableView
					rowKey='id'
					rowClassName={(record) => (item.dynamicProperties && item.dynamicProperties.state && item.dynamicProperties.state == "invalid") ? this.rowCss : ''}
					mode={this.props.mode}
					setBusy={this.setBusy}
					hideToolbar={ToolbarVisibility.hideSearch | ToolbarVisibility.hidePagination | ToolbarVisibility.hideModifyButtons}
					columns={this.modificationColumns}
					model={dataSet}
				/>
			)
			: null;
	}

	renderCarRentExtraField(item: QuoteEntryItem) {
		let occupancies = this.props.getOccupancy(item);
		let drivers = occupancies.filter(m => m.age > 16); // TODO: Paxes older than 16 years

		return item.productType == 2 ? <div key={`${item.id}`} className='card card-borderless mt-3'>
			<Row gutter={16} key={`${item.id} offices`}>
				<Col xs={24} md={12} key={`${item.id} officeP`}>
					<OfficeEditorController
						key={`${item.id} offices`}
						label="Pick up"
						required={true}
						canpPickup={true}
						supplierId={item.supplierId}
						mode={this.props.mode}
						onChanged={(value) => {
							item.dynamicProperties.officePickupId = value != null ? value.id : null;
						}}
						value={item.dynamicProperties.officePickupId}
						setBusy={this.setBusy}
					/>
				</Col>
				<Col xs={24} lg={12} key={`${item.id} officeD`}>
					<OfficeEditorController
						label="Drop off"
						required={true}
						canpDropOff={true}
						supplierId={item.supplierId}
						mode={this.props.mode}
						onChanged={(value) => {
							item.dynamicProperties.officeDropOffId = value != null ? value.id : null;
						}}
						value={item.dynamicProperties.officeDropOffId}
						setBusy={this.setBusy}
					/>
				</Col>
			</Row>
			<Row gutter={16} key={`${item.id} drivers`}>
				<Col xs={24} key={`${item.id} drive`}>
					<DropdownView
						required
						label="Driver"
						disabled={this.props.mode === 'display'}
						model={item.dynamicProperties.driver}
						onChanged={(newVal) => {
							item.dynamicProperties.driver = newVal.id;
						}}
						keyField="id"
						textField="name"
						options={drivers.slice()}
					/>
				</Col>
			</Row>
		</div> : null;
	}

	renderFlightExtraField(item: QuoteEntryItem) {
		if (item.productType == 4) {
			let legs: FlightLegResult[] = item.dynamicProperties.flightLegs;
			if (legs && legs.length > 0) {
				const columns: Column[] = [
					{
						key: 'startDate',
						dataIndex: ' ',
						title: 'Departure Date',
						render: (text: any, record: FlightLegResult) => {
							return this.formatService.toString('small-date-time', record.startDate);
						}
					},
					{
						key: 'DepartureAirport',
						dataIndex: '',
						title: 'Departure Airport',
						render: (text: any, record: FlightLegResult) => {
							return record.departureAirport ? record.departureAirport.code : ''
						}
					},
					{
						key: 'ArrivalAirport',
						dataIndex: '',
						title: 'Arrival Airport',
						render: (text: any, record: FlightLegResult) => {
							return record.destinationAirport ? record.destinationAirport.code : ''
						}
					},
					{
						key: 'departure times',
						dataIndex: '',
						title: 'All departure times',
						render: (text: any, record: FlightLegResult) => {
							return this.formatService.toString('small-time', record.startDate);
						}
					},
				]
				const dataSet = new DataLocalService(legs as any);
				return <DataTableView
					rowKey={(record: FlightSearchResult) => record.id}
					mode="display"
					setBusy={this.props.setBusy}
					hideToolbar={ToolbarVisibility.hideSearch | ToolbarVisibility.hideModifyButtons | ToolbarVisibility.hidePagination}
					columns={columns}
					model={dataSet}
					selection={
						false
					}
				/>
			}
			return null;
		}
		return null
	}
	protected renderTour = (item: QuoteEntryItem) => {
		const json = item.dynamicProperties;

		return (
			<TourInfoCard
				parent={item}
				isPackage={json.isPackage}
				mode={this.props.mode}
				productName={(i: QuoteEntryItem) => i.productDisplayName}
				toDate={(i: QuoteEntryItem) => i.dateTo}
				fromDate={(i: QuoteEntryItem) => i.dateFrom}
				priceRenderer={(text: any, record: any) => {
					return this.renderCurrency(record.dynamicProperties.sellPriceResult.currencyIsoCode, text);
				}}
				selection={false}
				getSubProcKeys={i => i.id}
				subProducts={this.props.model.items.filter(i => i.parentEntryId == item.id).sort((x, y) => moment.utc(x.dateFrom).isBefore(moment.utc(y.dateFrom)) ? -1 : 1)}
				extraColumn={
					{
						key: 'cmd',
						dataIndex: '',
						title: '',
						render: (text: any, record: QuoteEntryItem) => {
							let status;
							let style: any = {
							};
							switch (record.status) {
								case 1:
								case 2:
									status = 'primary';
									style.backgroundColor = '#ffffcd';
									style.borderColor = 'black';
									style.color = 'black';
									break;
								case 3:
									status = 'primary';
									style.backgroundColor = '#28a745';
									style.borderColor = '#28a745';
									break;
								case 4:
								case 5:
									status = 'danger';
									break;
							}
							const erratas = this.isVisibleErrataButton(record);
							return (
								<Button.Group>
									<NoteView
										identityService={this.props.identityService}
										mode={this.props.mode}
										noteProperty="notes"
										record={record.dynamicProperties}
										setBusy={this.setBusy}
									/>
									{
										erratas && <Button
											icon="alert"
											onClick={() => this.showErratasDialog(record)}
										>
										</Button>
									}
									<UserTasksModalView
										useLoginFilter={true}
										setBusy={this.setBusy}
										modelType='quoteEntry'
										modelId={record.id}
										state={record.entity.entityState}
										buttonProps={{ icon: "check-square" }}
										buttonText={null}
										daedline={moment.utc().add(1, "day").toDate()}
										assignedToId={this.props.identityService.userId}
										priority={1}
										titleTask="Task Title"
									/>
									<Button
										disabled={this.props.mode == 'display' || this.props.model.isDirty}
										type={status}
										style={style}
										onClick={this.loadStateHistory(record)}>
										{record.statusText}
									</Button>
								</Button.Group>
							);
						}
					}
				}
			/>
		);
	}

	protected expandedRowRender = (item: QuoteEntryItem) => {

		switch (item.productTypeText) {
			case 'Accommodation':
				return (
					<React.Fragment>
						<AccommodationInfoCard
							productId={item.productId}
							setBusy={this.setBusy}
							modifications={this.renderModifications(item)} />
					</React.Fragment>
				);
			case 'CarRent':
				let carRent = item.productType === 2 ? this.renderCarRentExtraField(item) : null;
				let modification = this.renderModifications(item);
				return (
					<React.Fragment>
						{modification}
						{carRent}
					</React.Fragment>
				);
			case 'Flights':
				let flights = this.renderFlightExtraField(item);
				return <React.Fragment>
					{flights}
				</React.Fragment>
			case 'General':
				break;
			case 'Transfer':
				break;
			case 'Tour':
				return this.renderTour(item);
		}
	}

	private validateProductManually(): string[] {
		const message = [];

		if (this.validator.isEmpty(this.productManually.costPrice)) {
			message.push('Cost Price is required');
		}
		if (!this.validator.isNumber(this.productManually.costPrice) && !this.validator.isNumberString(this.productManually.costPrice as any)) {
			message.push('Cost Price must be a number');
		}
		if (this.validator.isEmpty(this.productManually.costPriceResult.currencyIsoCode)) {
			message.push('Cost currency is required');
		}
		if (this.validator.isEmpty(this.productManually.sellPrice)) {
			message.push('Sell Price is required');
		}
		if (!this.validator.isNumber(this.productManually.sellPrice) && !this.validator.isNumberString(this.productManually.sellPrice as any)) {
			message.push('Sell Price must be a number');
		}
		if (this.validator.isEmpty(this.productManually.sellPriceResult.currencyIsoCode)) {
			message.push('Sell currency is required');
		}
		if (this.productManually.productType < 6 && this.validator.isEmpty(this.productManually.productCategory)) {
			message.push('Product Category is required');
		}
		if (this.productManually.productType < 6 && this.validator.isEmpty(this.productManually.productServiceType)) {
			message.push('Product Service is required');
		}
		if (this.productManually.productType == null) {
			message.push('Product Type is required');
		}
		if (this.validator.isEmpty(this.productManually.productDisplayName)) {
			message.push('Product Name is required');;
		}
		if (this.validator.isEmpty(this.productManually.supplierId)) {
			message.push('Supplier is required');
		}
		if (this.productManually.productType === 2) {
			const carRent = this.productManually as CarRentProductManually;
			if (this.validator.isEmpty(carRent.officeDropOffId)) {
				message.push('Pick up is required');
			}
			if (this.validator.isEmpty(carRent.officeDropOffId)) {
				message.push('Drop off is required');
			}
			if (this.validator.isEmpty(carRent.driver)) {
				message.push('Driver is required');
			}
		}
		if (this.productManually.productType === 3) {
			const transfer = this.productManually as TransferProductManually;
			if (this.validator.isEmpty(transfer.originPlaceId)) {
				message.push('Pick up is required');
			}
			if (this.validator.isEmpty(transfer.destinationPlaceId)) {
				message.push('Drop off is required');
			}
		}

		return message;
	}

	async updateQuoteAndCommissionForProductManually() {
		await this.props.updateCommissions();
		this.props.updateQuote();
	}

	private onTabChange = (key: string): void => {
		if (key !== 'Search') {
			const paxes = this.getPaxes();
			let prod: ProductManually = {
				dateFrom: moment.utc(this.props.quote.reservationDate).startOf('day').toDate(),
				dateTo: moment.utc(this.props.quote.reservationDate).startOf('day').toDate(),
				productDisplayName: '',
				occupancies: paxes,
				requiresConfirmation: true,
				productType: -1,
				costPrice: 0,
				costPriceResult: {
					breakdown: [],
					currencyIsoCode: this.props.currencyCode,
					exchangeRate: 1,
					isCommissionable: false,
					isPriceLive: true,
					sourceCurrencyIsoCode: this.props.currencyCode,
					totalPrice: 0,
					paxs: []
				},
				productCategory: undefined,
				productServiceType: undefined,
				sellPrice: 0,
				sellPriceResult: {
					breakdown: [],
					currencyIsoCode: this.props.currencyCode,
					exchangeRate: 1,
					isCommissionable: false,
					isPriceLive: true,
					sourceCurrencyIsoCode: this.props.currencyCode,
					totalPrice: 0,
					paxs: []
				},
				supplierId: undefined,
				supplierTitle: ''
			};
			this.productManually = observable(prod);
			this.disableSearchButton = false;
			this.currentSearch = undefined;
			this.setState({ selectedProducts: undefined, taxId: undefined });
		} else {
			this.currentSearch = undefined;
			this.setState({ selectedProducts: undefined, taxId: undefined });
			this.productManually = undefined;
		}
	}

	handleTabsAddProduct = (key: string): void => {
		const prod = toJS(this.productManually);
		prod.productType = parseInt(key);
		switch (key) {
			case '1':
				const accommodation = prod as AccommodationProductManually;
				accommodation.rating = 0;
				break;
			case '2':
				const carRent = prod as CarRentProductManually;
				const driver = this.props.paxs.find(e => e.clientId === this.props.quote.clientId).id;
				carRent.driver = driver;
				carRent.officeDropOffId = undefined;
				carRent.officePickupId = undefined;
				break;
			case '3':
				const transfer = prod as TransferProductManually;
				transfer.duration = 0;
				transfer.originPlaceId = undefined;
				transfer.destinationPlaceId = undefined;
				break;
			case '4':
				break;
			case '7':
				const salesAdjustment = this.props.model.items.find(e => e.productType === 7);
				if (salesAdjustment) {
					prod.supplierId = salesAdjustment.supplierId;
					prod.supplierTitle = salesAdjustment.supplierTitle;
					prod.costPrice = salesAdjustment.costPrice;
					prod.costPriceResult = salesAdjustment.dynamicProperties.costPriceResult;
					prod.sellPrice = salesAdjustment.sellPrice;
					prod.sellPriceResult = salesAdjustment.dynamicProperties.sellPriceResult;
					prod.salePriceGross = salesAdjustment.salePriceGross;
					prod.salesmanProfit = salesAdjustment.salesmanProfit;
				}
				prod.productDisplayName = 'Sales Adjustment';
				prod.dateFrom = this.props.quote.reservationDate;
				prod.dateTo = this.props.quote.reservationDate;
				prod.requiresConfirmation = false;
				break;
		}
		this.productManually = observable(prod);
	}

	private renderSearchProductModal = () => {
		const buttons = [
			<Button key="cancel" onClick={this.closeSearchDialog} icon="close" type="danger">Cancel</Button>
		];
		//const taxSelection = (
		//	<TaxEditorController
		//		label={null}
		//		required
		//		setBusy={this.setBusy}
		//		mode="edit"
		//		value={this.state.taxId}
		//		onChanged={(newValue) => {
		//			if (newValue != null) {
		//				this.setState({ taxId: newValue.id });
		//			}
		//		}}
		//	/>
		//);
		const addToBasketButton = (
			<Button key="confirm"
				type="primary"
				icon="plus"
				onClick={this.acceptSearchDialog}
				disabled={this.productManually == null
					? (
						this.state.selectedProducts == null
						|| this.state.selectedProducts.some(p =>
							p.selected.sell == null
							|| p.selected.sell.totalPrice == null
							|| isNaN(p.selected.sell.totalPrice)
						)
						|| this.disableSearchButton
						|| this.disableAddButton
					)
					: this.productManually.productType < 0}
			>Add Product(s)</Button>
		);
		if (this.productManually != null) {
			buttons.unshift(addToBasketButton);
		} else if (this.currentSearch == null) {
			buttons.unshift(
				<Button
					key="search"
					icon={this.searchProducts ? 'loading' : "search"}
					type="primary"
					onClick={this.onSearchClick}
					disabled={this.productManually == null ? (this.searchProducts || this.disableSearchButton) : false}
				>Search</Button>
			);
		} else /*if (this.currentSearch.type != 'Accommodation' && this.currentSearch.type != 'Tour')*/ {
			buttons.unshift(addToBasketButton);
		}

		const paxes = this.getPaxes();
		const spinIndicator = (
			<React.Fragment>
				<ZuluIcon type="loading" spin={true} style={{ fontSize: 32 }} />
				<br />
				{
					this.state.cancelSearch != null &&
					<Button
						type="danger"
						icon="close"
						shape="circle"
						className="mt-1"
						size="small"
						onClick={() => {
							this.state.cancelSearch.cancel("You cancel the search");
							this.setState({ cancelSearch: undefined });
						}}
						style={{ top: -33 }}
					/>
				}
			</React.Fragment>
		);

		return (
			<Drawer
				visible={this.showSearchModal}
				onClose={this.closeSearchDialog}
				title="Add Product"
				mask={false}
				maskClosable={false}
				width="calc(100% - 80px)"
				style={{ overflow: 'auto' }}
			>
				<Spin spinning={this.state.isBusy} size="large" indicator={spinIndicator}>
					<Tabs
						onChange={this.onTabChange}
						activeKey={this.productManually ? "Build" : "Search"}
					>
						<TabPane tab={<span><ZuluIcon type="search" />Search</span>} key="Search">
							<SearchProductsController
								cancelSearch={this.cancelSearch}
								bookingDate={this.props.quote.reservationDate}
								languageCode={this.props.quote.languageCode}
								showSearchText={true}
								onOpen={this.onOpen}
								paxs={paxes}
								setBusy={this.setBusy}
								onProductSelected={this.onProductSelected}
								onSearchSelected={this.onSearchSelected}
								onSearch={this.onSearch}
								marketId={this.props.quote.marketId}
								searchProducts={this.searchProducts}
								disableAddButton={(val) => this.disableAddButton = val}
							>
								{buttons}
							</SearchProductsController>
						</TabPane>
						<TabPane tab={<span><ZuluIcon type="build" />Build</span>} key="Build">
							<BuildProductView
								productManually={this.productManually}
								paxes={paxes}
								setBusy={this.setBusy}
								quote={this.props.quote}
								entries={this.props.model.items.slice()}
								currencyCode={this.props.currencyCode}
								handleTabsAddProduct={this.handleTabsAddProduct}
							>
								<Button.Group>
									{buttons}
								</Button.Group>
							</BuildProductView>
						</TabPane>
					</Tabs>
				</Spin>
			</Drawer>
		);
	};

	private cancelSearch = (cancelSearch: CancelTokenSource) => this.setState({ cancelSearch });

	private onSearchClick = () => this.searchProducts = true;

	private onOpen = () => this.disableSearchButton = true;

	private onSearch = (search: Search) => this.currentSearch = search;

	private onSearchSelected = () => {
		this.searchProducts = false;
		this.currentSearch = null;
		this.disableSearchButton = false;
		this.setState({ selectedProducts: undefined, taxId: undefined });
	}

	private onProductSelected = (selection: ResultSelection[], search: Search) => {
		this.setState({ selectedProducts: selection, taxId: this.props.quote.defaultTaxId });
		this.currentSearch = search;
		this.disableSearchButton = false;
	}

	@autobind
	private cancelOccupancyDialog(ev?: React.MouseEvent<HTMLButtonElement>) {
		if (ev != null) {
			ev.preventDefault();
		}
		this.showOccuppancyModal = false;
		this.entriesPax = null;
	}

	@autobind
	private acceptOccupancyDialog(ev?: React.MouseEvent<HTMLButtonElement>) {
		if (ev != null) {
			ev.preventDefault();
		}
		const toUpdate = this.needToUpdate(this.props.model.items, this.entriesPax);
		if (toUpdate.length > 0) {
			this.updateCurrentEntries(toUpdate, this.entriesPax);
			this.props.onEditQuoteEntryField(toUpdate, true);
		}
		this.entriesPax = null;
		this.showOccuppancyModal = false;
	}

	updateCurrentEntries(currentEntries: QuoteEntryItem[], occupancyEntries: EntryOccupancyPax[]) {
		for (let occupancy of occupancyEntries) {
			let entry = currentEntries.find(o => o.id == occupancy.entryId);
			if (entry) {
				entry.dynamicProperties.occupancy = occupancy.occupancy;
				entry.dynamicProperties.costPriceResult.breakdown = occupancy.breakdownCost;
				entry.dynamicProperties.sellPriceResult.breakdown = occupancy.breakdownSell;
			}
		}
	}

	needToUpdate(currentEntries: QuoteEntryItem[], occupancyEntries: EntryOccupancyPax[]): QuoteEntryItem[] {
		const result = [];
		for (let occupancyEntry of occupancyEntries) {
			let entry = currentEntries.find(o => o.id == occupancyEntry.entryId);
			if (entry) {
				const occupancy: QuoteEntryOccupancy[] = entry.dynamicProperties.occupancy;
				const entryOccupancy = occupancyEntry.occupancy;
				if (
					occupancy.every(e => entryOccupancy.some(o => o.id === e.id))
					&& entryOccupancy.every(e => occupancy.some(o => o.id !== e.id))
				) {
					continue;
				}
				result.push(entry);
			}
		}
		return result;
	}

	getEntriesPaxOccupancy(entries: QuoteEntryItem[]) {
		let res = entries.filter(e => e.productType !== 6)
			.map(e => {
				const occupancy = e.dynamicProperties.occupancy.filter(p => this.props.paxs.some(pax => pax.id === p.id));
				const cloneOccupancy = e.dynamicProperties.occupancy.map(e => toJS(e));
				const row: EntryOccupancyPax = {
					entryId: e.id,
					title: e.productDisplayName,
					dateFrom: moment.utc(e.dateFrom.toISOString().replace(/T.*/, '')).toDate(),
					dateTo: moment.utc(e.dateTo.toISOString().replace(/T.*/, '')).toDate(),
					occupancy: cloneOccupancy,
					supplierId: e.supplierId,
					productId: e.productId,
					productExternalId: e.externalId,
					breakdownCost: e.dynamicProperties.costPriceResult.breakdown ? e.dynamicProperties.costPriceResult.breakdown.map(x => x).slice() : [],
					breakdownSell: e.dynamicProperties.sellPriceResult.breakdown ? e.dynamicProperties.sellPriceResult.breakdown.map(x => x).slice() : [],
				};
				for (const pax of this.props.paxs) {
					row[pax.id] = occupancy.some(p => p.id === pax.id);
				}
				return row;
			});
		return res;
	}

	entriesPax: EntryOccupancyPax[];
	renderOccupancyModal = () => {
		const paxes = this.getPaxes();

		return (
			<Modal
				style={{ top: 20 }}
				width="80%"
				visible={this.showOccuppancyModal}
				onCancel={this.cancelOccupancyDialog}
				title="Occupancies"
				maskClosable={false}
				footer={[
					<Button
						key="confirm"
						type="primary"
						onClick={this.acceptOccupancyDialog}>
						OK
				</Button>
				]}>
				{
					<PaxProductOccupancyView
						entries={this.entriesPax}
						setBusy={this.setBusy}
						paxs={paxes}
						mode={this.props.mode}
					/>
				}
			</Modal>
		);
	};

	render() {
		if (this.props.model == null) {
			return null;
		}

		const menu = [
			{
				key: "sort",
				name: "Sort by dates",
				icon: "sort-ascending",
				disabled: this.props.model.items.length === 0 || this.props.mode === "display",
				onClick: (e: any) => {
					this.props.model.updateOrders();
				}
			},
			{
				key: "Occupancies",
				name: "Occupancies",
				icon: "team",
				disabled: this.props.model.items.length === 0,
				onClick: (e: any) => {
					this.entriesPax = this.getEntriesPaxOccupancy(this.props.model.items);
					this.showOccuppancyModal = true;
				}
			},
			{
				key: "autoconnet",
				name: "AutoConnect Transfer",
				icon: "icon-train",
				disabled: this.props.mode === "display" || this.props.model.items.length === 0,
				onClick: (e: any) => {
					this.showAutoConectModal = true;
				}
			},
		]

		return (
			<div>
				{this.renderSearchProductModal()}
				{this.renderQuoteEntryStatusModal()}
				{this.renderOccupancyModal()}
				{this.renderErratasModal()}
				{this.renderAutoConnetModal()}
				<DataTableView
					columns={this.columns}
					mode={this.props.mode}
					setBusy={this.setBusy}
					model={this.props.model}
					clientFilter={this.getParentItems}
					rowKey="id"
					rowClassName={(record) => (record.dynamicProperties && record.dynamicProperties.state && record.dynamicProperties.state == "invalid") ? this.rowCss : ''}
					hideToolbar={ToolbarVisibility.hideSearch | ToolbarVisibility.hidePagination}
					expandedRowRender={this.expandedRowRender}
					createNewItem={() => {
						this.showSearchModal = true;
						return Promise.resolve();
					}}
					beforeDeleteItem={this.props.checkProductDelete}
					afterDeleteItem={this.props.deleteProducts}
					afterRestoreItem={this.props.restoreProducts}
					toolbarLeftItems={menu}
				/>
			</div>
		);
	}

	private getParentItems = (e: QuoteEntryItem) => {
		const parent = this.props.model.items.find(i => i.id === e.parentEntryId);
		return e.parentEntryId == null || (e.productType !== 6 && e.productType !== 7 && (!!parent && parent.productType !== 9));
	}

	private setBusy = (isBusy: boolean): void => {
		this.setState({ isBusy });
		//this.props.setBusy(isBusy);
	}
}

const mapStateToProps = (state: IApplicationState): PropsFromState => {
	return {
		currencies: state.currencies,
	};
};

const connected = connect(
	mapStateToProps,
)(QuoteEntryView);
export { connected as QuoteEntryView };



// WEBPACK FOOTER //
// ./src/app/bookings/quote-entries/quote-entry.view.tsx